import { createContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Product } from "src/api/types";
import { MatchParam } from "src/constants/matches";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetProductBySlugQuery } from "src/api/services/productsApi";
import { useTranslation } from "react-i18next";

export const ProductContext = createContext<Product | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

const ProductProvider = ({ children }: Props) => {
  const { t } = useTranslation();
  const { product } = useParams<MatchParam<"PRODUCT_NAME">>();
  const { variant } = useParams<{ variant?: string | undefined }>();
  const { data, isLoading } = useGetProductBySlugQuery(product ?? skipToken);

  useEffect(() => {
    if (product != null) {
      localStorage.setItem("product", product);
      if (variant != null) {
        localStorage.setItem("variant", variant);
      }
    }
  }, [product, variant]);

  const value = useMemo(() => {
    if (data == null) return;

    return {
      ...data,
      name: t(`products.${data.slug}.name`, data.display_name || data.name),
    };
  }, [data, t]);

  if (isLoading === true) {
    return null;
  }

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

export default ProductProvider;
